import { breakpointValues } from '@v4/utils/theme'
import {
	DESKTOP_TOOLBAR_HEIGHT,
	LINK_LIST,
	MOBILE_TOOLBAR_HEIGHT,
	TOOLBAR_OFFSET,
	MERGE_TAGS,
	MERGE_TAG_GROUPS,
	EDITOR_VARIANT,
	MOBILE_TOOLBAR_WIDTH,
	DESKTOP_TOOLBAR_WIDTH,
	SMALL_MOBILE_TOOLBAR_HEIGHT,
} from './useEditor.const'

export const getLinkList = shortcuts => {
	const variables = _.intersection(_.keys(shortcuts), _.keys(LINK_LIST))

	return _.isEmpty(variables)
		? undefined
		: _.reduce(
				variables,
				(acc, item) => {
					const link = LINK_LIST[item]
					if (link) {
						acc.push(link)
					}

					return acc
				},
				[],
		  )
}

const addMergeTagsToList = ({ list, variables, tagGroups, tags }) => {
	if (_.isEmpty(variables) || _.isEmpty(tags)) {
		return null
	}

	const getGroupSettings = group => tagGroups?.[group] || MERGE_TAG_GROUPS[group]

	const variableSet = new Set(variables)
	variableSet.forEach(variable => {
		const setting = tags[variable]
		if (!setting) {
			return
		}

		_.forEach(setting.groups, (group, index, groups) => {
			const nextGroup = groups[index + 1]
			const groupSettings = getGroupSettings(group)
			const path = _.slice(groups, 0, index + 1).join('.groups.')
			let groupItem = _.get(list, path)

			if (!groupItem) {
				groupItem = {
					title: groupSettings?.title || _.startCase(group),
					order: groupSettings?.order ?? 1000,
				}
				_.set(list, path, groupItem)
			}

			if (nextGroup) {
				const nextGroupSettings = getGroupSettings(nextGroup)
				groupItem.groups = groupItem.groups || {
					[nextGroup]: {
						title: nextGroupSettings?.title || _.startCase(nextGroup),
						order: nextGroupSettings?.order ?? 1000,
					},
				}
			} else {
				groupItem.items = groupItem.items || []
				groupItem.items.push({
					title: setting.title,
					value: variable,
					searchLabel: setting.searchLabel,
					label: setting.label,
				})

				groupItem.items = _.sortBy(groupItem.items, ({ value }) => tags[value]?.order ?? 1000)
			}
		})

		variableSet.delete(variable)
	})

	return variableSet
}

const parseTagGroups = groups =>
	_.orderBy(groups, ['order']).map(item => {
		if ('groups' in item) {
			return {
				title: item.title,
				menu: parseTagGroups(item.groups),
			}
		}

		return {
			title: item.title,
			menu: item.items,
		}
	})

export const getMergeTagsList = (shortcuts, { categoryScoreMergeTags, leadAnswerMergeTags, quizMergeTags } = {}) => {
	const excludedKeys = _.difference(_.keys(LINK_LIST), ['email'])
	const variables = _.difference(_.keys(shortcuts), excludedKeys)
	if (_.isEmpty(variables)) {
		return []
	}

	const list = {}

	const variableSet = addMergeTagsToList({
		list,
		variables,
		tags: MERGE_TAGS,
	})

	if (categoryScoreMergeTags) {
		addMergeTagsToList({
			list,
			variables: _.keys(categoryScoreMergeTags.shortcuts),
			tagGroups: categoryScoreMergeTags.groups,
			tags: categoryScoreMergeTags.tags,
		})
	}

	if (leadAnswerMergeTags) {
		addMergeTagsToList({
			list,
			variables: _.keys(leadAnswerMergeTags.shortcuts),
			tagGroups: leadAnswerMergeTags.groups,
			tags: leadAnswerMergeTags.tags,
		})
	}

	if (quizMergeTags) {
		addMergeTagsToList({
			list,
			variables: _.keys(quizMergeTags.shortcuts),
			tagGroups: quizMergeTags.groups,
			tags: quizMergeTags.tags,
		})
	}

	const mergeTagsList = parseTagGroups(list)

	if (variableSet?.size) {
		const menu = []
		variableSet.forEach(variable => {
			menu.push({
				value: variable,
				title: _.capitalize(variable).replace(/_/g, ' '),
			})
		})
		mergeTagsList.push({
			title: 'Other',
			menu,
		})
	}

	return mergeTagsList
}

export const setContainerPosition = (
	container,
	relativeElement,
	classes,
	variant = EDITOR_VARIANT.FULL,
	selectedNode,
) => {
	if (!container || !relativeElement || !classes) {
		return
	}

	const selectedElement = selectedNode && relativeElement.contains(selectedNode) ? selectedNode : relativeElement

	const $element = container
	$element.className = classes.toolbar
	const { left, right, width } = relativeElement.getBoundingClientRect()
	const { top, bottom } = selectedElement.getBoundingClientRect()
	const leftPosition = left + window.scrollX
	const rightPosition = right + window.scrollX
	const topPosition = top + window.scrollY
	const bottomPosition = bottom + window.scrollY
	const { clientWidth } = document.body
	$element.style.position = 'absolute'
	$element.style.zIndex = 9999
	$element.style.width = 0
	$element.style.height = 0

	const isMobile = !!window.matchMedia(`(max-width: ${breakpointValues.sm}px)`).matches
	const toolbarWidth = isMobile ? MOBILE_TOOLBAR_WIDTH[variant] : DESKTOP_TOOLBAR_WIDTH[variant]
	const toolbarHeight =
		((variant === EDITOR_VARIANT.MOBILE && SMALL_MOBILE_TOOLBAR_HEIGHT) ||
			(isMobile ? MOBILE_TOOLBAR_HEIGHT : DESKTOP_TOOLBAR_HEIGHT)) + TOOLBAR_OFFSET
	const externalLeftPosition = Math.max(leftPosition - (toolbarWidth - width) / 2, 2)
	const externalRightPosition = Math.min(rightPosition + (toolbarWidth - width) / 2, clientWidth - 2)

	if (toolbarWidth < width) {
		$element.classList.add(classes.leftToolbar)
		$element.style.left = `${leftPosition + (width - toolbarWidth) / 2}px`
	} else if (externalLeftPosition <= clientWidth - toolbarWidth) {
		$element.classList.add(classes.leftToolbar)
		$element.style.left = `${externalLeftPosition}px`
	} else if (externalRightPosition >= toolbarWidth) {
		$element.classList.add(classes.rightToolbar)
		$element.style.left = `${externalRightPosition}px`
	} else {
		$element.classList.add(classes.centerToolbar)
		$element.style.left = `${clientWidth / 2}px`
	}

	if (top >= toolbarHeight) {
		$element.classList.add(classes.topToolbar)
		$element.style.top = `${topPosition}px`
	} else if (window.innerHeight - bottom >= toolbarHeight) {
		$element.classList.add(classes.bottomToolbar)
		$element.style.top = `${bottomPosition}px`
	} else {
		$element.style.position = 'fixed'
		$element.classList.add(classes.fixedToolbar)
		$element.style.top = '0'
	}
}

export const isHtmlEmpty = value =>
	['', null, undefined, '<br>', '<div><br></div>', '<p><br></p>', '<div></div>', '<p></p>'].includes(value)
